import React, { useRef } from "react";
import { useSelector } from "react-redux";
import Table from "../../components/Table/Table";
import { getAllSubscriptions } from "../../services/thunks/subscriptionThunks";
import { formatJoinedDate } from "../../utils/dateFormating";

const tableColumns = [
  {
    title: <div style={{ paddingLeft: "20px" }}>Customer Name</div>,
    dataIndex: "customerName",
    key: "customerName",
    minWidth: "150px",
    render: (text) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2] pl-[20px]">
        {text || "N/A"}
      </div>
    ),
  },
  {
    title: "Email",
    dataIndex: "email",
    key: "email",
    minWidth: "200px",
    render: (text) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {text || "N/A"}
      </div>
    ),
  },
  {
    title: "Current Plan",
    dataIndex: "planType",
    key: "planType",
    minWidth: "120px",
    render: (text) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {text || "N/A"}
      </div>
    ),
  },
  {
    title: "Start Date",
    dataIndex: "startDate",
    key: "startDate",
    minWidth: "130px",
    render: (date) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {date ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }) : "N/A"}
      </div>
    ),
  },
  {
    title: "Expiration Date",
    dataIndex: "endDate",
    key: "endDate",
    minWidth: "130px",
    render: (date) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {date ? new Date(date).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        }) : "N/A"}
      </div>
    ),
  },
  
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    minWidth: "100px",
    render: (status) => (
      <div className="min-h-[32px] flex items-center text-sm font-[400] font-public-sans">
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            status === "Active"
              ? " text-[#07C01D] "
              : " text-[#FF4D4F]"
          }`}
        >
          {status==="Expired"? "Inactive" :(status ==="Active"?"Active":"N/A")}
        </span>
      </div>
    ),
  },
  
];

// Payment History columns for expandable sub-table
const paymentHistoryColumns = [
  {
    title: "Invoice ID",
    dataIndex: "invoiceId",
    key: "invoiceId",
    minWidth: "100px",
    render: (text) => (
      <div className="text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {text || "N/A"}
      </div>
    ),
  },
  {
    title: "Amount Paid",
    dataIndex: "amountPaid",
    key: "paymentAmountPaid",
    minWidth: "100px",
    render: (amount) => (
      <div className="text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {typeof amount === "number"
          ? `${amount.toFixed(2)}`
          : "N/A"}
      </div>
    ),
  },
  {
    title: "Plan Type",
    dataIndex: "planType",
    key: "paymentPlanType",
    minWidth: "100px",
    render: (text) => (
      <div className="text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {text || "N/A"}
      </div>
    ),
  },
  {
    title: "Coupon Applied",
    dataIndex: "couponApplied",
    key: "paymentCoupon",
    minWidth: "100px",
    render: (coupon) => (
       <div
          className={`h-[28px] min-w-[40px] flex items-center justify-center text-sm font-[400] font-public-sans ${
            coupon ? "text-[#07C01D] border border-[#07C01D] rounded-[15px] px-3" : ""
          }`}
        >
          {coupon || "—"}
        </div>
    ),
  },
  {
    title: "Coupon Discount",
    dataIndex: "couponDiscount",
    key: "paymentCouponDiscount",
    minWidth: "100px",
    render: (discount) => (
      <div className="text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {typeof discount === "number"
          ? `${discount.toFixed(2)}`
          : "—"}
      </div>
    ),
  },
  {
    title: "Payment Date",
    dataIndex: "paidAt",
    key: "paidAt",
    minWidth: "100px",
    render: (date) => (
      <div className="text-sm font-[400] font-public-sans text-[#E2E2E2]">
        {date
          ? new Date(date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "2-digit",
            })
          : "N/A"}
      </div>
    ),
  },
  {
    title: "Invoice",
    dataIndex: "invoiceUrl",
    key: "invoiceUrl",
    minWidth: "100px",
    render: (url) => (
      <div className="text-sm font-[400] font-public-sans">
        {url ? (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-400 hover:text-blue-300 underline"
          >
            View Invoice
          </a>
        ) : (
          <span className="text-[#E2E2E2]">N/A</span>
        )}
      </div>
    ),
  },
];

const SubscriptionTable = React.forwardRef(
  ({ filters, refreshTrigger }, ref) => {
    const tableRef = useRef(null);
    
    // Create a custom query params function
    const customQueryParams = (params) => {
      console.log("Raw params received:", params);
      
      const queryParams = {
        ...params,
        planType: filters?.planType !== "All" ? filters?.planType : undefined,
        status: filters?.status !== "All" ? filters?.status : undefined,
        startDate: filters?.startDate || undefined,
        endDate: filters?.endDate || undefined,
      };
      
      // Clean up undefined values
      Object.keys(queryParams).forEach(key => {
        if (queryParams[key] === undefined) {
          delete queryParams[key];
        }
      });
      return queryParams;
    };

    // Sub-table configuration for payment history
    const subTableConfig = {
      columns: paymentHistoryColumns,
      dataField: "paymentHistory", // Field containing the payment history array
      rowKey: "_id",
    };

    // Function to check if a row should be expandable
    const expandableRowCheck = (record) => {
      return record.hasPaymentHistory === true && 
             Array.isArray(record.paymentHistory) && 
             record.paymentHistory.length > 0;
    };

    return (
      <div>
        <div ref={tableRef}>
          <Table
            columns={tableColumns}
            fetchData={getAllSubscriptions}
            filters={filters}
            refreshTrigger={refreshTrigger}
            customQueryParams={customQueryParams}
            headingBgColor="#0D141B"
            headingTextColor="#FFFFFF"
            initialPageSize={25}
            highlightedRowId={null}
            hasSubTable={true} // Enable expandable rows
            subTableConfig={subTableConfig} // Pass sub-table configuration
            expandableRowCheck={expandableRowCheck} // Custom check for expandable rows
            enableUrlParams={true} // Enable URL parameter handling for filter reset
            key={`table-${refreshTrigger}`} // Force re-render when refreshTrigger changes
          />
        </div>
      </div>
    );
  }
);

export default SubscriptionTable;